import React, { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { initVersionCheck } from "@/utils/versionCheck";
import ScrollToTop from "@/components/ScrollToTop";
import NativelyBackgroundSync from "@/components/NativelyBackgroundSync";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppSessionProvider } from "@/providers/AppSessionProvider";
import { AdminSimulateProvider } from "@/providers/AdminSimulateProvider";
import { DialogProvider } from "@/contexts/DialogContext";
import { OneSignalProvider } from "@/providers/OneSignalProvider";
import { ThemeProvider } from "@/providers/ThemeProvider";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import EmailVerified from "./pages/EmailVerified";
import Profile from "./pages/Profile";
import Track from "./pages/Track";
import Upgrade from "./pages/Upgrade";
import CreditsSuccess from "./pages/CreditsSuccess";
import SentryTest from "./pages/SentryTest";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import ReportBug from "./pages/ReportBug";
import ContactUs from "./pages/ContactUs";
import Tutorial from "./pages/Tutorial";
import Social from "./pages/Social";
import Settings from "./pages/Settings";
import Loading from "./pages/Loading";
import NotFound from "./pages/NotFound";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DeleteAccount from "./pages/DeleteAccount";
import About from "./pages/About";
import Onboarding from "./pages/Onboarding";
import Tour from "./pages/Tour";
import Promo from "./pages/Promo";
import Admin from "./pages/Admin";
import CreatorDashboard from "./pages/CreatorDashboard";
import SnapView from "./pages/SnapView";
import CreateSnap from "./pages/CreateSnap";
import Compare from "./pages/Compare";
import OAuthRedirect from "./pages/OAuthRedirect";
import Proof from "./pages/Proof";
import AndroidWaitlist from "./pages/AndroidWaitlist";
import Showcase from "./pages/Showcase";
import SubscriptionSync from "@/components/SubscriptionSync";
import HealthKitSync from "@/components/HealthKitSync";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AdminSimulateBanner } from "@/components/AdminSimulateBanner";
import { TutorialController } from "@/components/onboarding/TutorialController";
import { AppVisitTracker } from "@/components/AppVisitTracker";
import { useProfile } from "@/hooks/useProfile";
import { useAppSession } from "@/providers/AppSessionProvider";

// Configure QueryClient with better defaults for mobile apps
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry failed requests, but with a backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus immediately after background
      refetchOnWindowFocus: false,
      // Keep data fresh for 30 seconds
      staleTime: 30000,
      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Handle errors gracefully
      throwOnError: false,
    },
  },
});

// Tutorial wrapper component to access session context
const TutorialWrapper = () => {
  const { session } = useAppSession();
  const { data: userProfile } = useProfile(session?.user?.id);
  
  return <TutorialController username={userProfile?.username || null} />;
};

const App = () => {
  // Initialize version checking on app load
  useEffect(() => {
    initVersionCheck();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppSessionProvider>
          <AdminSimulateProvider>
            <AuthProvider>
              <OneSignalProvider>
                <DialogProvider>
                  <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <SubscriptionSync />
                  <HealthKitSync />
                  <BrowserRouter>
                  <AdminSimulateBanner />
                  <TutorialWrapper />
                  <AppVisitTracker />
                  <ScrollToTop />
                  <NativelyBackgroundSync />
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/callback" element={<Auth />} />
                  <Route path="/oauth-redirect" element={<OAuthRedirect />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/tour" element={<Tour />} />
                  <Route path="/promo" element={<Promo />} />
                  <Route path="/email-verified" element={<EmailVerified />} />
                  <Route path="/tutorial" element={<Tutorial />} />
                  <Route path="/loading" element={<Loading />} />
                  
                  <Route path="/track" element={<Track />} />
                  <Route path="/social" element={<Social />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/sentry-test" element={<SentryTest />} />
                  <Route path="/upgrade" element={<Upgrade />} />
                  <Route path="/credits-success" element={<CreditsSuccess />} />
                  <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                  <Route path="/report-bug" element={<ReportBug />} />
                  <Route path="/contact-us" element={<ContactUs />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/creator-dashboard" element={<CreatorDashboard />} />
                  <Route path="/snap/:snapId" element={<SnapView />} />
                  <Route path="/create-snap" element={<CreateSnap />} />
                  <Route path="/compare" element={<Compare />} />
                  <Route path="/proof" element={<Proof />} />
                  <Route path="/android" element={<AndroidWaitlist />} />
                  <Route path="/showcase" element={<Showcase />} />
                  <Route path="/:username" element={<Profile />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
                  </TooltipProvider>
                </DialogProvider>
              </OneSignalProvider>
            </AuthProvider>
          </AdminSimulateProvider>
        </AppSessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
